// Logo Manager Component
import React, { useState, useEffect, useRef } from 'react';
import { ImageIcon, Upload, Loader2, Save, RefreshCw, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { compressImage } from '../../utils/imageCompression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// mode: "all" (default) | "logos" | "reviews"
const LogoManager = ({ mode = "all" }) => {
  const [settings, setSettings] = useState({
    logo_header: '',
    logo_footer: '',
    logo_alt: 'SAE - Société Agro Équipement',
    favicon: '',
    show_reviews: true,
    reviews_rating: '5.0',
    reviews_count: '47',
    reviews_url: 'https://maps.app.goo.gl/GPaMHSSiUcEEy97D6'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ header: false, footer: false, favicon: false });
  
  const headerInputRef = useRef(null);
  const footerInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const showLogos = mode === "all" || mode === "logos";
  const showReviews = mode === "all" || mode === "reviews";

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, type) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      // Compress image before upload (logos are smaller, favicon should be small too)
      const maxWidth = type === 'favicon' ? 256 : 500;
      const compressedFile = await compressImage(file, { maxWidth, quality: 0.9 });
      
      const formData = new FormData();
      formData.append('file', compressedFile);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fieldName = type === 'header' ? 'logo_header' : type === 'footer' ? 'logo_footer' : 'favicon';
        setSettings(prev => ({
          ...prev,
          [fieldName]: data.url
        }));
        const typeLabels = { header: 'Logo header', footer: 'Logo footer', favicon: 'Favicon' };
        toast.success(`${typeLabels[type]} uploadé`);
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Erreur de connexion');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Logos enregistrés ! Rechargez la page pour voir les changements.');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {mode === "all" && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-light text-slate-800">Logos & Favicon</h2>
            <p className="text-sm text-slate-500 mt-1">Gérez les logos, le favicon et les avis Google</p>
          </div>
          <Button onClick={fetchSettings} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      )}

      {/* LOGOS SECTION */}
      {showLogos && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Header Logo */}
          <div className="border border-slate-200 rounded-lg p-6 bg-white">
            <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Logo Header (En-tête)
            </h3>
            
            <div className="bg-slate-800 rounded-lg p-4 mb-4 flex items-center justify-center min-h-[80px]">
              {settings.logo_header ? (
                <img 
                  src={settings.logo_header} 
                alt="Logo Header" 
                className="max-h-12 object-contain"
              />
            ) : (
              <span className="text-slate-400 text-sm">Aucun logo</span>
            )}
          </div>
          
          <div className="space-y-3">
            <input
              type="url"
              value={settings.logo_header || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, logo_header: e.target.value }))}
              placeholder="URL du logo ou uploadez une image"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            
            <input
              ref={headerInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(e.target.files[0], 'header')}
              className="hidden"
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => headerInputRef.current?.click()}
              disabled={uploading.header}
              className="w-full"
            >
              {uploading.header ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload en cours...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Uploader une image</>
              )}
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 mt-3">
            Recommandé : PNG transparent, hauteur max 48px
          </p>
        </div>

        {/* Footer Logo */}
        <div className="border border-slate-200 rounded-lg p-6 bg-white">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Logo Footer (Pied de page)
          </h3>
          
          <div className="bg-slate-800 rounded-lg p-4 mb-4 flex items-center justify-center min-h-[80px]">
            {settings.logo_footer ? (
              <img 
                src={settings.logo_footer} 
                alt="Logo Footer" 
                className="max-h-12 object-contain"
              />
            ) : settings.logo_header ? (
              <img 
                src={settings.logo_header} 
                alt="Logo Footer (utilise header)" 
                className="max-h-12 object-contain opacity-50"
              />
            ) : (
              <span className="text-slate-400 text-sm">Aucun logo</span>
            )}
          </div>
          
          <div className="space-y-3">
            <input
              type="url"
              value={settings.logo_footer || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, logo_footer: e.target.value }))}
              placeholder="URL du logo (vide = même que header)"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            
            <input
              ref={footerInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(e.target.files[0], 'footer')}
              className="hidden"
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => footerInputRef.current?.click()}
              disabled={uploading.footer}
              className="w-full"
            >
              {uploading.footer ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload en cours...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Uploader une image</>
              )}
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 mt-3">
            Laissez vide pour utiliser le même logo que le header
          </p>
        </div>
      </div>

      {/* Favicon */}
      <div className="border border-slate-200 rounded-lg p-6 bg-white">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          Favicon (Icône du navigateur)
        </h3>
        
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200">
              {settings.favicon ? (
                <img 
                  src={settings.favicon} 
                  alt="Favicon" 
                  className="w-10 h-10 object-contain"
                />
              ) : settings.logo_header ? (
                <img 
                  src={settings.logo_header} 
                  alt="Favicon (utilise logo)" 
                  className="w-10 h-10 object-contain opacity-50"
                />
              ) : (
                <span className="text-slate-400 text-xs">Aucun</span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1 text-center">Aperçu</p>
          </div>
          
          <div className="flex-1 space-y-3">
            <input
              type="url"
              value={settings.favicon || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, favicon: e.target.value }))}
              placeholder="URL du favicon ou uploadez une image"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            
            <input
              ref={faviconInputRef}
              type="file"
              accept="image/*,.ico"
              onChange={(e) => handleUpload(e.target.files[0], 'favicon')}
              className="hidden"
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => faviconInputRef.current?.click()}
              disabled={uploading.favicon}
            >
              {uploading.favicon ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Uploader</>
              )}
            </Button>
            
            <p className="text-xs text-slate-500">
              Recommandé : PNG ou ICO carré (32x32 ou 64x64 pixels). Laissez vide pour utiliser le logo header.
            </p>
          </div>
        </div>
      </div>

      {/* Alt Text */}
      <div className="border border-slate-200 rounded-lg p-6 bg-white">
        <h3 className="font-medium text-slate-800 mb-4">Texte alternatif (SEO)</h3>
        <input
          type="text"
          value={settings.logo_alt || ''}
          onChange={(e) => setSettings(prev => ({ ...prev, logo_alt: e.target.value }))}
          placeholder="Description du logo pour le SEO"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
        <p className="text-xs text-slate-500 mt-2">
          Ce texte sera affiché si l'image ne charge pas et aide le référencement
        </p>
      </div>
      </>
      )}

      {/* Google Reviews Section */}
      {showReviews && (
      <div className="border border-slate-200 rounded-lg p-6 bg-white">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-medium text-slate-800 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Avis Google
            </h3>
            <p className="text-sm text-slate-500 mt-1">Gérez le bandeau d'avis Google affiché sur le site</p>
          </div>
          
          {/* Toggle Switch */}
          <label className="flex items-center gap-3 cursor-pointer">
            <span className={`text-sm ${settings.show_reviews ? 'text-green-600' : 'text-slate-400'}`}>
              {settings.show_reviews ? 'Visible' : 'Masqué'}
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={settings.show_reviews}
                onChange={(e) => setSettings(prev => ({ ...prev, show_reviews: e.target.checked }))}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${settings.show_reviews ? 'bg-green-500' : 'bg-slate-300'}`}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.show_reviews ? 'translate-x-5' : ''}`} />
              </div>
            </div>
          </label>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
            <input
              type="text"
              value={settings.reviews_rating || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, reviews_rating: e.target.value }))}
              placeholder="5.0"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre d'avis</label>
            <input
              type="text"
              value={settings.reviews_count || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, reviews_count: e.target.value }))}
              placeholder="47"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lien Google Maps</label>
            <input
              type="url"
              value={settings.reviews_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, reviews_url: e.target.value }))}
              placeholder="https://maps.app.goo.gl/..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
        
        <p className="text-xs text-slate-500 mt-3">
          Mettez à jour ces valeurs manuellement pour refléter vos vrais avis Google
        </p>
      </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Enregistrer</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LogoManager;
